import { Position } from "@/services/backend/rlt/treatments/types/position";
import { useEffect, useRef, useState } from "react";
import {
  UpdateCycleDiscountActiveRequest,
  UpdateCycleDiscountPercentageRequest,
  useRltTreatmentUpdateCycleDiscountActiveMutation,
  useRltTreatmentUpdateCycleDiscountPercentageMutation,
} from "@/services/backend/rlt/treatments/api/treatment-api";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/lib/utils";
import { tableInputClass } from "@/shared/components/tables/table-input-class";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { useRefEffect } from "@/shared/hooks/use-ref-effect";

export function CycleDiscountCell({
  treatmentId,
  position,
  disabled = false,
}: {
  treatmentId: string;
  position: Position;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-end gap-2">
      <ActiveCheckbox
        treatmentId={treatmentId}
        position={position}
        disabled={disabled}
      />
      <PercentageInput
        treatmentId={treatmentId}
        position={position}
        disabled={disabled}
      />
    </div>
  );
}

function ActiveCheckbox({
  treatmentId,
  position,
  disabled = false,
}: {
  treatmentId: string;
  position: Position;
  disabled?: boolean;
}) {
  const [request, setRequest] = useState<UpdateCycleDiscountActiveRequest>({
    id: treatmentId,
    positionId: position.id,
    active: position.cycleDiscountActive,
  });

  const v = useRef(position.cycleDiscountActive);
  useEffect(() => {
    if (v.current !== position.cycleDiscountActive) {
      v.current = position.cycleDiscountActive;
      setRequest({
        ...request,
        active: position.cycleDiscountActive,
      });
    }
  }, [position.cycleDiscountActive, request]);

  const mutation = useRltTreatmentUpdateCycleDiscountActiveMutation();
  const { isLoading } = useDebouncedMutation(
    request,
    setRequest,
    mutation,
    {
      id: treatmentId,
      positionId: position.id,
      active: position.cycleDiscountActive,
    },
    { delay: 0 },
  );

  return (
    <Checkbox
      checked={request.active}
      onCheckedChange={(checked) =>
        setRequest({
          ...request,
          active: checked === "indeterminate" ? false : checked,
        })
      }
      disabled={isLoading || disabled}
    />
  );
}

function PercentageInput({
  treatmentId,
  position,
  disabled = false,
}: {
  treatmentId: string;
  position: Position;
  disabled?: boolean;
}) {
  const [request, setRequest] = useState<UpdateCycleDiscountPercentageRequest>({
    id: treatmentId,
    positionId: position.id,
    percentage: position.cycleDiscountPercentage,
  });
  const mutation = useRltTreatmentUpdateCycleDiscountPercentageMutation();
  useDebouncedMutation(request, setRequest, mutation, {
    id: treatmentId,
    positionId: position.id,
    percentage: position.cycleDiscountPercentage,
  });

  useRefEffect(position.cycleDiscountPercentage, () => {
    setRequest({
      ...request,
      percentage: position.cycleDiscountPercentage,
    });
  });

  return (
    <div>
      <Input
        type="number"
        className={cn(tableInputClass, "inline-flex w-12")}
        value={position.cycleDiscountActive ? request.percentage : 0}
        onChange={(e) =>
          setRequest({
            ...request,
            percentage: Number(e.target.value),
          })
        }
        disabled={disabled}
      />
      <span className="ml-1">%</span>
    </div>
  );
}
